import { UIKit } from "@/shared/ui";
/**
 * Виджет «Свойства» — правая панель редактора.
 *
 * Показывает параметры выделенного объекта (стена / проём / угол / прибор / светильник /
 * кабель) и позволяет задать их числом, а не мышью: для чертежа это основной способ ввода.
 * У выключателя здесь видно, какие светильники он зажигает, у светильника — какой
 * выключатель его включает: группа освещения читается из проводки, а не хранится.
 * Не хранит паспорт проекта и экспликацию: это отдельное меню верхней панели.
 */
import { pipeLength } from "@/widgets/plan";
import { type CatalogItem } from "@/shared/catalog";
import { pipeEndCatalogItem } from "@/widgets/plumbing";
import {
	WaterMedium,
	acceptsMedium,
	defaultPipe,
	pipeItemById,
	waterMediumColor,
	waterMediumLabel,
} from "@/shared/catalog";
import { usePipesStore } from "@/shared/store/layers";
import { SelectionKind, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";
import { remove } from "@/shared/store/actions";
import { formatMeters } from "@/shared/lib/geometry";
import { CATALOG } from "@/shared/catalog";

export type PropertiesPanelCatalogItem = CatalogItem;

export const PipeProperties = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const graph = layers.walls;
	const selection = useEditorStore((s) => s.selection);
	const setSelection = useEditorStore((s) => s.setSelection);
	const updatePipe = usePipesStore((s) => s.updatePipe);
	const pushHistory = useProjectStore((s) => s.pushHistory);

	// Хелпер для updateFixture в зависимости от каталога

	const onDelete = () => {
		// Удаление кнопкой отменяется по Ctrl+Z, как и клавишей Delete
		pushHistory();
		remove(selection);
		setSelection(null);
	};

	if (!selection) return null;
	if (selection.kind === SelectionKind.Pipe) {
		const pipe = layers.pipes.items[selection.id];
		if (!pipe) return null;
		const plumbing = layers.plumbing;
		const nameOf = (id: string) => pipeEndCatalogItem(plumbing, id)?.name ?? "Точка";
		const length = pipeLength(graph, plumbing, pipe);
		const material = pipeItemById(pipe.material ?? defaultPipe().id) ?? defaultPipe();
		// Среду можно сменить, только если обе точки её принимают
		const mediaAllowed = [WaterMedium.Cold, WaterMedium.Hot].filter(
			(medium) =>
				acceptsMedium(pipeEndCatalogItem(plumbing, pipe.from), medium) &&
				acceptsMedium(pipeEndCatalogItem(plumbing, pipe.to), medium),
		);
		return (
			<>
				<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
					Труба {waterMediumLabel(pipe.medium)}
				</UIKit.Text>
				<div className="flex flex-col gap-1 rounded-lg bg-slate-50 px-2.5 py-2 text-xs text-ink">
					<UIKit.Text as="span">
						От: <b className="text-ink">{nameOf(pipe.from)}</b>
					</UIKit.Text>
					<UIKit.Text as="span">
						До: <b className="text-ink">{nameOf(pipe.to)}</b>
					</UIKit.Text>
					<UIKit.Text as="span">Поворотов на трассе: {pipe.points.length}</UIKit.Text>
				</div>

				{mediaAllowed.length > 1 && (
					<div className="flex flex-col gap-1">
						<UIKit.Text
							as="span"
							className="text-xs font-semibold tracking-label text-ink uppercase"
						>
							Вода
						</UIKit.Text>
						<div className="flex gap-1.5">
							{mediaAllowed.map((medium) => (
								<UIKit.Button
									key={medium}
									type="button"
									className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-2 py-1.75 text-xs${
										pipe.medium === medium
											? " border-accent bg-accent-soft font-medium text-accent"
											: " border-border bg-surface text-ink hover:border-slate-300 hover:text-ink"
									}`}
									onClick={() => {
										pushHistory();
										updatePipe(pipe.id, { medium });
									}}
								>
									<UIKit.Text
										as="span"
										className="size-2.5 rounded-full"
										style={{ background: waterMediumColor(medium) }}
									/>
									{waterMediumLabel(medium)}
								</UIKit.Button>
							))}
						</div>
					</div>
				)}

				<div className="flex flex-col gap-1">
					<UIKit.Text as="span" className="text-xs font-semibold tracking-label text-ink uppercase">
						Материал
					</UIKit.Text>
					{CATALOG.pipe.items.map((item) => (
						<UIKit.Button
							key={item.id}
							type="button"
							title={item.description}
							className={`flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-left text-xs text-ink hover:bg-[#f4f7fb]${
								material.id === item.id
									? " border-accent bg-accent-soft font-medium text-accent"
									: ""
							}`}
							onClick={() => {
								pushHistory();
								updatePipe(pipe.id, { material: item.id });
							}}
						>
							<UIKit.Text
								as="span"
								className="size-2.5 shrink-0 rounded-sm"
								style={{ background: item.color }}
							/>
							<UIKit.Text as="span" className="min-w-0 flex-1">
								{item.name}
							</UIKit.Text>
							<UIKit.Text as="span" className="text-xs text-ink tabular-nums">
								Ø{item.diameter}
							</UIKit.Text>
						</UIKit.Button>
					))}
				</div>

				{length && (
					<div className="flex flex-col gap-1 rounded-lg border border-border px-2.5 py-2 text-xs text-ink">
						<UIKit.Text as="span">
							По полу: <b className="text-ink tabular-nums">{formatMeters(length.plan)}</b>
						</UIKit.Text>
						<UIKit.Text as="span">
							Подъёмы к точкам:{" "}
							<b className="text-ink tabular-nums">{formatMeters(length.rises)}</b>
						</UIKit.Text>
						<UIKit.Text as="span">
							Всего трубы: <b className="text-ink tabular-nums">{formatMeters(length.total)}</b>
						</UIKit.Text>
					</div>
				)}
				<UIKit.Text className="text-xs leading-copy text-ink">
					Трасса идёт по полу в стяжке и у каждой точки поднимается по стене до высоты вывода. Чтобы
					изменить трассу, удалите трубу и проложите заново.
				</UIKit.Text>
				<UIKit.Button
					type="button"
					className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-danger hover:bg-red-50"
					onClick={onDelete}
				>
					Удалить трубу
				</UIKit.Button>
			</>
		);
	}

	return null;
};

/** Список типов раздела каталога — сменить тип объекта, не переставляя его. */
export function CatalogTypeList({
	items,
	current,
	onPick,
}: {
	items: CatalogItem[];
	current: string;
	onPick: (itemId: string) => void;
}) {
	if (items.length === 0) return null;
	return (
		<>
			<UIKit.Text
				as="span"
				className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
			>
				Тип
			</UIKit.Text>
			<div className="flex flex-col gap-1">
				{items.map((item) => (
					<UIKit.Button
						key={item.id}
						type="button"
						className={`flex items-center gap-2 rounded-lg border border-transparent px-2.25 py-1.75 text-left text-sm text-ink hover:bg-[#f3f5f9]${
							current === item.id ? " border-accent bg-accent-soft text-accent" : ""
						}`}
						onClick={() => onPick(item.id)}
					>
						<UIKit.Text
							as="span"
							className="size-2.5 shrink-0 rounded-sm"
							style={{ background: item.color }}
						/>
						<UIKit.Text as="span" className="min-w-0 flex-1">
							{item.name}
						</UIKit.Text>
					</UIKit.Button>
				))}
			</div>
		</>
	);
}
